/**
 * InAppBrowserGuide.js
 * A premium overlay to guide users to open the app in an external browser.
 * Uses vanilla JS and Lucide icons.
 */
import { isInAppBrowser } from '../../utils/userAgent.js';
import { createElement } from 'lucide'; // Direct import from lucide

export const InAppBrowserGuide = () => {
    // 0. Safety Check for Execution Timing
    const run = () => {
        // 1. Check Condition
        if (!isInAppBrowser()) {
            // Optional: Allow forced render for testing via URL param ?test-guide=true
            const params = new URLSearchParams(window.location.search);
            if (!params.get('test-guide')) return;
        }

        // 2. Create Overlay
        // Updated: "Cosmic Neon Bee" Guide (v0.361)
        const overlay = document.createElement('div');
        // FORCE Z-INDEX MAX to prevent being hidden by Modals or Loaders
        overlay.style.zIndex = "2147483647";
        overlay.className = "fixed inset-0 flex flex-col items-center justify-center bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-900 via-[#1a0b2e] to-black backdrop-blur-md overflow-hidden animate-fade-in";


        // 2.1 Inject Custom Styles for "Cosmic Bee" Animations
        const style = document.createElement('style');
        style.innerHTML = `
        @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-15px) rotate(5deg); }
        }
        @keyframes buzz-wings {
            0% { transform: rotate(-20deg) scaleY(1); opacity: 0.8; }
            50% { transform: rotate(20deg) scaleY(0.6); opacity: 0.5; }
            100% { transform: rotate(-20deg) scaleY(1); opacity: 0.8; }
        }
        @keyframes stardust-drift {
            0% { transform: translate(0, 0) scale(1); opacity: 1; box-shadow: 0 0 5px cyan; }
            100% { transform: translate(-25px, 25px) scale(0); opacity: 0; box-shadow: 0 0 0 transparent; }
        }
        @keyframes neon-pulse {
            0%, 100% { filter: drop-shadow(0 0 5px rgba(139,92,246,0.8)); }
            50% { filter: drop-shadow(0 0 15px rgba(6,182,212,1)); }
        }
        @keyframes twinkle {
            0%, 100% { opacity: 0.3; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
        .star { position: absolute; background: white; border-radius: 50%; animation: twinkle 3s infinite ease-in-out; }
    `;
        document.head.appendChild(style);

        // 2.2 Create Stardust (Background Context)
        const starContainer = document.createElement('div');
        starContainer.className = "absolute inset-0 pointer-events-none";
        for (let i = 0; i < 40; i++) {
            const star = document.createElement('div');
            star.className = "star";
            const size = Math.random() * 2 + 1;
            star.style.width = `${size}px`;
            star.style.height = `${size}px`;
            star.style.top = `${Math.random() * 100}%`;
            star.style.left = `${Math.random() * 100}%`;
            star.style.animationDelay = `${Math.random() * 3}s`;
            star.style.opacity = Math.random();
            starContainer.appendChild(star);
        }
        overlay.appendChild(starContainer);

        // 3. Render HTML Content
        const content = document.createElement('div');
        content.className = "relative z-10 flex flex-col items-center text-center p-8 max-w-md w-full";
        content.innerHTML = `
        <!-- Main Content -->
        <div class="mb-12 relative animate-float">
            <h1 class="text-2xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-white to-violet-300 mb-8 drop-shadow-2xl text-center leading-tight">
                이 작은 창문에<br/>
                <span class="text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)]">썰화월드</span>를<br/>
                가두지 마세요.
            </h1>
        </div>
        
        <p class="text-lg text-gray-300 text-center leading-relaxed font-medium">
            온전한 작품 감상을 위해<br/>
             <strong class="text-cyan-400 font-bold underline decoration-2 underline-offset-4 cursor-pointer hover:text-cyan-300 transition-colors">[다른 브라우저로 열기]</strong>를<br/>
            해주세요.
        </p>
    `;
        overlay.appendChild(content);

        // 4. The 'Cosmic Neon Bee' Character (Bottom Right)
        const character = document.createElement('div');
        character.className = "fixed bottom-12 right-6 flex flex-col items-center z-[10000] pointer-events-none";
        character.innerHTML = `
        <!-- 1. Stardust Trail (Particles) -->
        <div class="absolute -top-4 -left-4 w-1.5 h-1.5 bg-cyan-400 rounded-full animate-[stardust-drift_1.5s_ease-out_infinite]"></div>
        <div class="absolute -top-2 -left-6 w-1 h-1 bg-violet-400 rounded-full animate-[stardust-drift_2s_ease-out_infinite_0.3s]"></div>
        <div class="absolute -top-6 -left-2 w-1 h-1 bg-white rounded-full animate-[stardust-drift_1.8s_ease-out_infinite_0.6s]"></div>
        
        <!-- 2. Neon Bee Wrapper (Floating) -->
        <div class="relative animate-float cursor-pointer">
            
            <!-- Wings (Buzzing - Neon Style) -->
            <div class="absolute -top-5 left-1 w-5 h-6 bg-cyan-100/50 rounded-[50%] blur-[0.5px] animate-[buzz-wings_0.1s_linear_infinite] origin-bottom-right border border-cyan-200/50 shadow-[0_0_10px_rgba(6,182,212,0.5)]"></div>
            <div class="absolute -top-5 right-1 w-5 h-6 bg-cyan-100/50 rounded-[50%] blur-[0.5px] animate-[buzz-wings_0.1s_linear_infinite_reverse] origin-bottom-left border border-cyan-200/50 shadow-[0_0_10px_rgba(6,182,212,0.5)]"></div>

            <!-- Body (SVG - Cosmic Neon) -->
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 100 100" class="animate-[neon-pulse_3s_infinite_alternate]">
                <!-- Stinger -->
                <path d="M50 85 L45 95 L55 95 Z" fill="#4c1d95" stroke="#8b5cf6" stroke-width="1" />
                
                <!-- Body Base (Dark Violet) -->
                <ellipse cx="50" cy="55" rx="30" ry="35" fill="#2e1065" stroke="#8b5cf6" stroke-width="1.5" />
                
                <!-- Neon Stripes (Glowing Cyan) -->
                <!-- Using Path with stroke-dasharray/glow filter naturally handled by CSS class/colors here -->
                <path d="M25 45 Q50 55 75 45 L78 52 Q50 62 22 52 Z" fill="#06b6d4" filter="drop-shadow(0 0 2px cyan)" />
                <path d="M28 60 Q50 70 72 60 L74 67 Q50 77 26 67 Z" fill="#06b6d4" filter="drop-shadow(0 0 2px cyan)" />
                
                <!-- Face Area -->
                <!-- Neon Eyes -->
                <circle cx="40" cy="35" r="3.5" fill="#22d3ee" filter="drop-shadow(0 0 3px cyan)" /> <!-- Left Eye -->
                <circle cx="60" cy="35" r="3.5" fill="#22d3ee" filter="drop-shadow(0 0 3px cyan)" /> <!-- Right Eye -->
                
                <!-- Smile (Neon Pink) -->
                <path d="M45 42 Q50 45 55 42" stroke="#d946ef" stroke-width="2" fill="none" stroke-linecap="round" filter="drop-shadow(0 0 2px magenta)" /> 
                
                <!-- Antennae (Violet Glowing) -->
                <path d="M35 25 Q30 10 20 15" stroke="#a78bfa" stroke-width="2" fill="none" stroke-linecap="round" />
                <path d="M65 25 Q70 10 80 15" stroke="#a78bfa" stroke-width="2" fill="none" stroke-linecap="round" />
                <!-- Antennae Tips -->
                <circle cx="20" cy="15" r="2" fill="#d946ef" />
                <circle cx="80" cy="15" r="2" fill="#d946ef" />
            </svg>
        </div>

        <!-- 3. Text Label -->
        <span class="mt-1 text-[10px] md:text-xs font-bold text-cyan-300 tracking-widest uppercase animate-pulse drop-shadow-[0_0_5px_rgba(6,182,212,0.8)] bg-black/70 px-3 py-1 rounded-full backdrop-blur-sm border border-cyan-500/50 shadow-[0_0_10px_rgba(6,182,212,0.2)]">
            OPEN HERE
        </span>
    `;
        overlay.appendChild(character);

        document.body.appendChild(overlay);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run);
    } else {
        run();
    }
};
